/**
 * Resource API Client
 *
 * API client for managing resource revision operations (documents, trainings, etc.)
 * Following the MATIC pattern with singleton instance and private convert methods.
 */

import {AuthenticatedApiClient} from '@matic/lib/api/authenticated-api-client';
import {isValidUuid} from '@matic/lib/utils';
import {numericToLetterRevision} from '@/matic/features/vault/revisions/utils/revision-transforms';
import type {
  RevisionResponse,
  RevisionListResponse,
  Revision,
  RevisionList,
  ContentSnapshot,
  ContentSnapshotResponse,
  DocumentContentSnapshot,
  TrainingProgramContentSnapshot,
  UpdateRevisionRequest,
  RevisionStatus,
} from '@/matic/features/vault/revisions/types';
import {
  type Approval,
  type ApprovalResponse,
  type ApprovalListResponse,
  type ApprovalList,
  type SubmitApprovalRequest,
  type ApprovalDecisionRequest,
  type ReassignApproversRequest,
  type ApprovalStatus,
  type ApprovalRule,
  ApprovalRule as ApprovalRuleEnum,
} from '@/matic/features/vault/approvals/types';

/**
 * Resource API client for revision and approval operations
 */
export class ResourceApiClient extends AuthenticatedApiClient {
  private get basePath(): string {
    return '/api/v1/vault/resources';
  }

  /**
   * Get all revisions for a resource
   *
   * @param params - Request parameters object
   * @returns Promise<RevisionList> - List of revisions in camelCase format
   */
  async getRevisions(parameters: {
    resourceId: string;
    latestRevisionId?: string;
    publishedRevisionId?: string;
  }): Promise<RevisionList> {
    const {resourceId, latestRevisionId, publishedRevisionId} = parameters;
    // Validate inputs
    if (!isValidUuid(resourceId)) {
      throw new Error('Resource ID must be a valid UUID');
    }

    try {
      const endpoint = `${this.basePath}/${resourceId}/revisions`;
      const apiResponse = await this.get<RevisionListResponse>(endpoint);

      return this.convertRevisionListResponse(
        apiResponse,
        latestRevisionId,
        publishedRevisionId
      );
    } catch (error) {
      this.handleResourceError(error, 'getRevisions');
    }
  }

  /**
   * Get a single revision by ID
   *
   * @param params - Request parameters object
   * @returns Promise<Revision> - Single revision in camelCase format
   */
  // Deprecated. Don't use generic revision or resource endpoints
  async getRevision(parameters: {
    resourceId: string;
    revisionId: string;
    latestRevisionId?: string;
    publishedRevisionId?: string;
  }): Promise<Revision> {
    const {resourceId, revisionId, latestRevisionId, publishedRevisionId} =
      parameters;
    // Validate inputs
    if (!isValidUuid(resourceId)) {
      throw new Error('Resource ID must be a valid UUID');
    }

    if (!isValidUuid(revisionId)) {
      throw new Error('Revision ID must be a valid UUID');
    }

    try {
      const endpoint = `${this.basePath}/${resourceId}/revisions/${revisionId}`;
      const apiResponse = await this.get<RevisionResponse>(endpoint);

      return this.convertRevisionResponse(
        apiResponse,
        latestRevisionId,
        publishedRevisionId
      );
    } catch (error) {
      this.handleResourceError(error, 'getRevision');
    }
  }

  /**
   * Update a revision (changelog only)
   *
   * @param resourceId - Resource ID (e.g., document ID)
   * @param revisionId - Revision ID to update
   * @param updateData - Update payload (changelog)
   * @returns Promise<Revision> - Updated revision in camelCase format
   */
  async updateRevision(
    resourceId: string,
    revisionId: string,
    updateData: UpdateRevisionRequest
  ): Promise<Revision> {
    // Validate inputs
    if (!isValidUuid(resourceId)) {
      throw new Error('Resource ID must be a valid UUID');
    }

    if (!isValidUuid(revisionId)) {
      throw new Error('Revision ID must be a valid UUID');
    }

    try {
      const endpoint = `${this.basePath}/${resourceId}/revisions/${revisionId}`;
      const apiResponse = await this.put<RevisionResponse>(
        endpoint,
        updateData
      );

      return this.convertRevisionResponse(apiResponse);
    } catch (error) {
      this.handleResourceError(error, 'updateRevision');
    }
  }

  /**
   * Publish a revision (Approved → Published)
   *
   * @param resourceId - Resource ID (e.g., document ID)
   * @param revisionId - Revision ID to publish
   * @returns Promise<Revision> - Published revision in camelCase format
   */
  async publishRevision(
    resourceId: string,
    revisionId: string
  ): Promise<Revision> {
    // Validate inputs
    if (!isValidUuid(resourceId)) {
      throw new Error('Resource ID must be a valid UUID');
    }

    if (!isValidUuid(revisionId)) {
      throw new Error('Revision ID must be a valid UUID');
    }

    try {
      const endpoint = `${this.basePath}/${resourceId}/revisions/${revisionId}/publish`;
      const apiResponse = await this.post<RevisionResponse>(endpoint, {});

      return this.convertRevisionResponse(apiResponse);
    } catch (error) {
      this.handleResourceError(error, 'publishRevision');
    }
  }

  // ========================================
  // Approval Methods
  // ========================================

  /**
   * Submit a document revision for approval
   *
   * @param resourceId - Document ID (UUID)
   * @param request - Approval request data
   * @returns Promise<Approval> - Created approval workflow
   *
   * @throws Error if:
   * - Resource not found (404)
   * - User lacks permission (403)
   * - Validation fails (400)
   * - Server error (500)
   *
   * @example
   * ```typescript
   * const approval = await resourceApi.submitForApproval(
   *   documentId,
   *   {
   *     approver_ids: [user1.id, user2.id],
   *     approval_rule: 'all',
   *     comment: 'Ready for review'
   *   }
   * );
   * ```
   */
  async submitForApproval(
    resourceId: string,
    request: SubmitApprovalRequest
  ): Promise<Approval> {
    if (!isValidUuid(resourceId)) {
      throw new Error('Resource ID must be a valid UUID');
    }

    if (!request.approver_ids || request.approver_ids.length === 0) {
      throw new Error('At least one approver is required');
    }

    try {
      const endpoint = `${this.basePath}/${resourceId}/approvals`;

      const apiResponse = await this.post<ApprovalResponse>(endpoint, request);

      return this.convertApprovalResponse(apiResponse);
    } catch (error) {
      this.handleResourceError(error, 'submitForApproval');
    }
  }

  /**
   * Get current approval workflow status for a document
   *
   * @param resourceId - Document ID (UUID)
   * @returns Promise<Approval | null> - Current approval or null if none exists
   *
   * @throws Error if:
   * - User lacks permission (403)
   * - Server error (500)
   *
   * Returns null (not error) if no approval workflow exists (404)
   *
   * @example
   * ```typescript
   * const approval = await resourceApi.getApprovalStatus(documentId);
   *
   * if (approval) {
   *   console.log('Status:', approval.status); // pending, approved, rejected
   * } else {
   *   console.log('No approval workflow for this document');
   * }
   * ```
   */
  async getApprovalStatus(resourceId: string): Promise<Approval | undefined> {
    if (!isValidUuid(resourceId)) {
      throw new Error('Resource ID must be a valid UUID');
    }

    try {
      const endpoint = `${this.basePath}/${resourceId}/approvals`;

      const apiResponse = await this.get<ApprovalResponse>(endpoint);

      return this.convertApprovalResponse(apiResponse);
    } catch (error) {
      // 404 means no approval workflow exists (valid state)
      if ((error as {status?: number})?.status === 404) {
        return undefined;
      }

      this.handleResourceError(error, 'getApprovalStatus');
    }
  }

  /**
   * Make an approval decision (approve or reject)
   *
   * @param resourceId - Document ID (UUID)
   * @param decision - Approval decision data
   * @returns Promise<Approval> - Updated approval workflow
   *
   * @throws Error if:
   * - User not an assigned approver (403)
   * - Approval not in pending status (400)
   * - Comment missing (400)
   * - Server error (500)
   *
   * @example
   * ```typescript
   * const updatedApproval = await resourceApi.makeApprovalDecision(
   *   documentId,
   *   {
   *     decision: 'approved',
   *     comment: 'Technical review complete',
   *     signature_name: user.name,
   *     signature_confirmed: true
   *   }
   * );
   * ```
   */
  async makeApprovalDecision(
    resourceId: string,
    decision: ApprovalDecisionRequest
  ): Promise<Approval> {
    if (!isValidUuid(resourceId)) {
      throw new Error('Resource ID must be a valid UUID');
    }

    if (!decision.comment || decision.comment.trim().length === 0) {
      throw new Error('Comment is required for all approval decisions');
    }

    try {
      const endpoint = `${this.basePath}/${resourceId}/approvals/decision`;

      const apiResponse = await this.post<ApprovalResponse>(endpoint, decision);

      return this.convertApprovalResponse(apiResponse);
    } catch (error) {
      this.handleResourceError(error, 'makeApprovalDecision');
    }
  }

  /**
   * Get pending approvals for the current user
   *
   * NOTE: Requires backend enhancement (GET /approvals/pending endpoint)
   * See research/backend-updates-required.md
   *
   * @param tenantId - Tenant ID (UUID)
   * @param options - Query options (limit, offset)
   * @returns Promise<ApprovalList> - Paginated approval list
   *
   * @throws Error if backend endpoint not available
   *
   * @example
   * ```typescript
   * const pending = await resourceApi.getPendingApprovals(tenantId, { limit: 10 });
   * console.log(`You have ${pending.total} pending approvals`);
   * ```
   */
  async getPendingApprovals(
    tenantId: string,
    options: {
      limit?: number;
      offset?: number;
    } = {}
  ): Promise<ApprovalList> {
    if (!tenantId || tenantId === 'undefined') {
      throw new Error('Invalid tenant ID');
    }

    const queryParameters = new URLSearchParams();

    if (options.limit !== undefined) {
      queryParameters.append('limit', options.limit.toString());
    }

    if (options.offset !== undefined) {
      queryParameters.append('offset', options.offset.toString());
    }

    const endpoint = `/api/v1/vault/approvals/pending${queryParameters.toString() ? `?${queryParameters.toString()}` : ''}`;

    try {
      const apiResponse = await this.get<ApprovalListResponse>(endpoint);

      return {
        items: apiResponse.items.map((item) =>
          this.convertApprovalResponse(item)
        ),
        total: apiResponse.total,
        limit: apiResponse.limit,
        offset: apiResponse.offset,
      };
    } catch (error) {
      // If endpoint doesn't exist (404), return empty list
      if ((error as {status?: number})?.status === 404) {
        console.warn('Pending approvals endpoint not available');
        return {
          items: [],
          total: 0,
          limit: options.limit ?? 20,
          offset: options.offset ?? 0,
        };
      }

      this.handleResourceError(error, 'getPendingApprovals');
    }
  }

  /**
   * Reassign approvers for an approval workflow
   *
   * @deprecated Use documentsApi.reassignApprovers() instead.
   * This method uses the generic /resources/ API which is being deprecated
   * in favor of document-specific APIs.
   *
   * New endpoint: PUT /documents/{documentId}/revisions/{revisionId}/approval/approvers
   *
   * @param resourceId - Document ID (UUID)
   * @param request - Reassignment request data
   * @returns Promise<Approval> - Updated approval workflow
   */
  async reassignApprovers(
    resourceId: string,
    request: ReassignApproversRequest
  ): Promise<Approval> {
    console.warn(
      'resourceApi.reassignApprovers() is deprecated. Use documentsApi.reassignApprovers() instead.'
    );

    if (!isValidUuid(resourceId)) {
      throw new Error('Resource ID must be a valid UUID');
    }

    const endpoint = `${this.basePath}/${resourceId}/approvals/approvers`;

    try {
      const apiResponse = await this.put<ApprovalResponse>(endpoint, request);

      return this.convertApprovalResponse(apiResponse);
    } catch (error) {
      // If route not registered, enhance with user-friendly message
      const status = (error as any)?.status;
      if (status === 404 || status === 501) {
        const apiError = new Error(
          'Reassign approvers feature is not yet available. Use documentsApi.reassignApprovers() instead.'
        );
        (apiError as any).status = status;
        throw apiError;
      }

      this.handleResourceError(error, 'reassignApprovers');
    }
  }

  // ========================================
  // Private Methods
  // ========================================

  /**
   * Convert content snapshot from backend to frontend format
   *
   * @param resourceType - Type of resource ("document", etc.)
   * @param snapshot - Backend content snapshot (snake_case)
   * @returns Frontend content snapshot (camelCase)
   */
  private convertContentSnapshot(
    resourceType: string,
    snapshot: ContentSnapshotResponse
  ): ContentSnapshot {
    switch (resourceType) {
      case 'document': {
        const docSnapshot = snapshot as DocumentContentSnapshot;
        return {
          name: docSnapshot.name,
          fileId: docSnapshot.file_id,
          contentType: docSnapshot.content_type,
          fileSize: docSnapshot.file_size,
          metadata: docSnapshot.metadata,
          fieldValues: docSnapshot.field_values as
            | Record<string, string | string[]>
            | undefined,
        };
      }

      case 'trainingprogram': {
        const trainingSnapshot = snapshot as TrainingProgramContentSnapshot;
        return {
          name: trainingSnapshot.name,
          createdBy: trainingSnapshot.created_by,
        };
      }

      default: {
        throw new Error(`Unsupported resource type: ${resourceType}`);
      }
    }
  }

  /**
   * Convert API RevisionResponse to frontend Revision type (camelCase)
   */
  private convertRevisionResponse(
    apiRevision: RevisionResponse,
    latestRevisionId?: string,
    publishedRevisionId?: string
  ): Revision {
    return {
      id: apiRevision.id,
      resourceType: apiRevision.resource_type,
      resourceId: apiRevision.resource_id,
      revisionNumber: apiRevision.revision_number,
      // Use revision_label from backend (if available), fallback to old transform for backward compatibility
      revisionLabel:
        apiRevision.revision_label ||
        numericToLetterRevision(apiRevision.revision_number),
      contentSnapshot: this.convertContentSnapshot(
        apiRevision.resource_type,
        apiRevision.content_snapshot
      ),
      changelog: apiRevision.changelog,
      status: apiRevision.status as RevisionStatus,
      createdBy: apiRevision.created_by,
      createdAt: apiRevision.created_at,
      updatedAt: apiRevision.updated_at,
      isCurrent: apiRevision.id === latestRevisionId,
      isPublished: apiRevision.id === publishedRevisionId,
    };
  }

  /**
   * Convert API RevisionListResponse to frontend RevisionList type
   */
  private convertRevisionListResponse(
    apiResponse: RevisionListResponse,
    latestRevisionId?: string,
    publishedRevisionId?: string
  ): RevisionList {
    return {
      items: apiResponse.items.map((item) =>
        this.convertRevisionResponse(
          item,
          latestRevisionId,
          publishedRevisionId
        )
      ),
      total: apiResponse.total,
    };
  }

  /**
   * Convert snake_case API response to camelCase frontend Approval type
   * Private method following MATIC pattern
   *
   * @param apiResponse - Raw API response
   * @returns Approval - Frontend-friendly type
   */
  private convertApprovalResponse(apiResponse: ApprovalResponse): Approval {
    // Map backend status to frontend
    // - "in_review" → "pending" (frontend terminology)
    // - "revoked" → "cancelled" (legacy data compatibility)
    let status: ApprovalStatus;
    if (apiResponse.status === 'in_review') {
      status = 'pending' as ApprovalStatus;
    } else if (apiResponse.status === 'revoked') {
      status = 'cancelled' as ApprovalStatus; // Map legacy "revoked" to "cancelled"
    } else {
      status = apiResponse.status as ApprovalStatus;
    }

    return {
      id: apiResponse.id,
      status,
      resourceId: apiResponse.resource_id,
      resourceType: apiResponse.resource_type,
      documentId: apiResponse.document_id,
      documentName: apiResponse.document_name,
      displayId: apiResponse.display_id,
      createdAt: apiResponse.created_at,
      completedAt: apiResponse.completed_at,

      // From backend enhancements (may be undefined if not implemented)
      approverIds: apiResponse.approver_ids ?? [],
      approvalRule:
        (apiResponse.approval_rule as ApprovalRule) ?? ApprovalRuleEnum.ALL,
      submittedBy: apiResponse.submitted_by,
      auditTrail: apiResponse.audit_trail,
    };
  }

  /**
   * Handle resource-specific errors following MATIC pattern
   */
  private handleResourceError(error: unknown, endpoint: string): never {
    console.error(`ResourceApiClient.${endpoint} failed:`, error);
    throw error;
  }
}

// ============================================================================
// Factory Function (Recommended Pattern)
// ============================================================================

/**
 * Cached singleton factory for ResourceApiClient
 *
 * Returns a cached instance per token to avoid unnecessary recreation.
 * This provides singleton efficiency while maintaining proper OIDC integration.
 *
 * @param token - Optional OIDC token from useApiToken()
 * @returns Cached ResourceApiClient instance
 *
 * @example
 * ```typescript
 * const token = useApiToken();
 * const api = getResourceApi(token); // Cached by token
 * const revision = await api.getRevision({ resourceId, revisionId });
 * ```
 */
const clientCache = new Map<string, ResourceApiClient>();

export function getResourceApi(token?: string): ResourceApiClient {
  const key = token ?? 'no-token';

  if (!clientCache.has(key)) {
    clientCache.set(key, new ResourceApiClient(token));
  }

  return clientCache.get(key)!;
}

// Legacy singleton - deprecated, use getResourceApi(token) instead
export const resourceApi = new ResourceApiClient();

export default resourceApi;

// Re-export types for convenience
export type {UpdateRevisionRequest} from '@/matic/features/vault/revisions/types';
export type {
  SubmitApprovalRequest,
  ApprovalDecisionRequest,
  ReassignApproversRequest,
} from '@/matic/features/vault/approvals/types';
